import React, { useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconGripVertical,
  IconInfoCircle,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDiscountPresets } from "../../../context/DiscountPresetsContext";
import { useResponsive } from "../../../context/ResponsiveContext";
import { DiscountPreset, MAX_QUICK_DISCOUNTS } from "../../../types/discounts";
import {
  formatDiscountOff,
  formatDiscountValue,
} from "../../../utils/discountFormatting";
import { moveQuickDiscount } from "../../../utils/discountPresetUtils";
import { notifyError } from "../../../utils/notify";
import { StandardTableShell } from "../../common/StandardTable";
import { DISCOUNT_TABLE_COLUMN_WIDTHS as COLUMN_WIDTHS } from "./discountTableColumns";
import cardStyles from "./DiscountPresetCard.module.css";
import actionStyles from "./DiscountPresetsSettings.module.css";
import styles from "./QuickDiscountsSettings.module.css";

export const QUICK_DISCOUNTS_TOOLTIP = `Shown as one-click buttons at checkout, in this order (up to ${MAX_QUICK_DISCOUNTS}).`;

// Lock dragging to the vertical axis (no @dnd-kit/modifiers dependency).
// Mirrors StaffReorderPanel.
const lockToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

interface QuickDiscountRowProps {
  preset: DiscountPreset;
  canManage: boolean;
  disabled: boolean;
  onRemove: (preset: DiscountPreset) => void;
}

/** dnd-kit wiring shared by the table row and the phone card. */
function useSortableQuickDiscount(
  preset: DiscountPreset,
  canManage: boolean,
  disabled: boolean,
) {
  const sortable = useSortable({
    id: preset.id,
    disabled: !canManage || disabled,
  });
  return {
    setNodeRef: sortable.setNodeRef,
    isDragging: sortable.isDragging,
    style: {
      transform: CSS.Transform.toString(sortable.transform),
      transition: sortable.transition,
    },
    handle: canManage ? (
      <ActionIcon
        variant="subtle"
        color="gray"
        size="lg"
        aria-label={`Drag ${preset.name}`}
        className={`${styles.handle} ${actionStyles.touchTarget}`}
        disabled={disabled}
        {...sortable.attributes}
        {...sortable.listeners}
      >
        <IconGripVertical size={18} />
      </ActionIcon>
    ) : null,
  };
}

const RemoveButton: React.FC<{
  preset: DiscountPreset;
  disabled: boolean;
  onRemove: (preset: DiscountPreset) => void;
}> = ({ preset, disabled, onRemove }) => (
  <ActionIcon
    variant="subtle"
    color="gray"
    size="lg"
    className={actionStyles.touchTarget}
    aria-label={`Remove ${preset.name} from quick discounts`}
    disabled={disabled}
    onClick={() => onRemove(preset)}
  >
    <IconX size={18} />
  </ActionIcon>
);

/** Desktop/tablet: a row lined up with the "All discounts" table columns. */
const QuickDiscountTableRow: React.FC<QuickDiscountRowProps> = ({
  preset,
  canManage,
  disabled,
  onRemove,
}) => {
  const { setNodeRef, isDragging, style, handle } = useSortableQuickDiscount(
    preset,
    canManage,
    disabled,
  );
  return (
    <Table.Tr
      ref={setNodeRef}
      style={style}
      className={styles.sortable}
      data-dragging={isDragging || undefined}
      data-testid="quick-discount-row"
    >
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          {handle}
          <Text size="sm" fw={500}>
            {preset.name}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td w={COLUMN_WIDTHS.type}>
        <Text size="sm" c="dimmed">
          {preset.discount_type}
        </Text>
      </Table.Td>
      <Table.Td w={COLUMN_WIDTHS.amount}>
        <Text size="sm">
          {formatDiscountValue(preset.discount_type, preset.amount)}
        </Text>
      </Table.Td>
      {canManage && (
        <Table.Td w={COLUMN_WIDTHS.actions}>
          <RemoveButton
            preset={preset}
            disabled={disabled}
            onRemove={onRemove}
          />
        </Table.Td>
      )}
    </Table.Tr>
  );
};

/** Phone: the same card as an "All discounts" preset, plus a drag handle. */
const QuickDiscountCard: React.FC<QuickDiscountRowProps> = ({
  preset,
  canManage,
  disabled,
  onRemove,
}) => {
  const { setNodeRef, isDragging, style, handle } = useSortableQuickDiscount(
    preset,
    canManage,
    disabled,
  );
  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`${cardStyles.card} ${styles.sortable}`}
      data-dragging={isDragging || undefined}
      data-testid="quick-discount-row"
    >
      {handle}
      <Box className={cardStyles.body}>
        <Text fw={600} className={cardStyles.name}>
          {preset.name}
        </Text>
        <Text size="sm" c="dimmed">
          {`${preset.discount_type} · ${formatDiscountValue(
            preset.discount_type,
            preset.amount,
          )}`}
        </Text>
      </Box>
      {canManage && (
        <Box className={cardStyles.actions}>
          <RemoveButton
            preset={preset}
            disabled={disabled}
            onRemove={onRemove}
          />
        </Box>
      )}
    </Box>
  );
};

export interface QuickDiscountsSettingsProps {
  /** `settingsDiscounts.canManagePresets`: add / remove / reorder. */
  canManage: boolean;
}

/**
 * Settings → Discounts "Quick discounts": up to MAX_QUICK_DISCOUNTS presets,
 * drag-to-reorder, shown in this order as one-click shortcuts in the
 * Transaction Modal / Quick Sale summary. Styled as a sibling of the "All
 * discounts" list (same heading, table columns and phone cards). Every change
 * PUTs the whole ordered list through DiscountPresetsContext (optimistic,
 * rolled back on failure).
 */
export default function QuickDiscountsSettings({
  canManage,
}: QuickDiscountsSettingsProps) {
  const { presets, quickDiscounts, setQuickDiscounts } = useDiscountPresets();
  const { isMobile } = useResponsive();
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const quickIds = quickDiscounts.map((preset) => preset.id);
  const addable = presets.filter((preset) => !quickIds.includes(preset.id));
  const canAdd =
    canManage && quickIds.length < MAX_QUICK_DISCOUNTS && addable.length > 0;

  const save = async (nextIds: string[]) => {
    // One save at a time (a keyboard drag can end mid-save), so responses
    // can't land out of order.
    if (saving) return;
    setSaving(true);
    try {
      await setQuickDiscounts(nextIds);
    } catch (err) {
      console.error("Error saving quick discounts:", err);
      notifyError(
        (err as Error | null)?.message ||
          "Couldn't save your quick discounts. Please try again.",
        { title: "Save failed" },
      );
    } finally {
      setSaving(false);
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const next = moveQuickDiscount(
      quickIds,
      String(active.id),
      String(over.id),
    );
    if (next) save(next);
  };

  const rowProps = (preset: DiscountPreset): QuickDiscountRowProps => ({
    preset,
    canManage,
    disabled: saving,
    onRemove: (removed) => save(quickIds.filter((id) => id !== removed.id)),
  });

  const rows = quickDiscounts.map((preset) =>
    isMobile ? (
      <QuickDiscountCard key={preset.id} {...rowProps(preset)} />
    ) : (
      <QuickDiscountTableRow key={preset.id} {...rowProps(preset)} />
    ),
  );

  // The DndContext wraps the whole list (not the <tbody>), so its hidden
  // screen-reader instructions don't land inside the table.
  const renderList = () => {
    if (quickDiscounts.length === 0) {
      return (
        <Text size="sm" c="dimmed" data-testid="quick-discounts-empty">
          No quick discounts yet.
        </Text>
      );
    }
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
        modifiers={[lockToVerticalAxis]}
      >
        <SortableContext
          items={quickIds}
          strategy={verticalListSortingStrategy}
        >
          {isMobile ? (
            <Stack gap="sm" aria-label="Quick discounts">
              {rows}
            </Stack>
          ) : (
            <StandardTableShell minWidth={480} ariaLabel="Quick discounts">
              <Table.Tbody>{rows}</Table.Tbody>
            </StandardTableShell>
          )}
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Group gap={6} wrap="nowrap">
          <Title order={5} fw={600}>
            Quick discounts
          </Title>
          <Tooltip
            label={QUICK_DISCOUNTS_TOOLTIP}
            multiline
            w={240}
            withArrow
            events={{ hover: true, focus: true, touch: true }}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="What are quick discounts?"
            >
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Tooltip>
          <Text size="sm" c="dimmed">
            {`${quickIds.length} of ${MAX_QUICK_DISCOUNTS}`}
          </Text>
        </Group>
        {canAdd && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="subtle"
                color="teal"
                size="xs"
                className={styles.addButton}
                leftSection={<IconPlus size={14} />}
                aria-label="Add quick discount"
                disabled={saving}
              >
                Add
              </Button>
            </Menu.Target>
            <Menu.Dropdown className={styles.addMenu}>
              {addable.map((preset) => (
                <Menu.Item
                  key={preset.id}
                  onClick={() => save([...quickIds, preset.id])}
                  rightSection={
                    <Text size="xs" c="dimmed">
                      {formatDiscountOff(preset.discount_type, preset.amount)}
                    </Text>
                  }
                >
                  {preset.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
      {renderList()}
    </Stack>
  );
}
